import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import events from 'store/events';
import { stateSelector } from 'store/selectors';
import {
  clear, solve, check, upsertSaves, initGame, fetchGameStart, fetchGameSuccess, fetchGameFailed,
  updateDirectory, unselect, select as selectCell, nextCell, nextClue, priorCell, priorClue, inputLetter,
  toggleDirection, shiftLeft, shiftRight, shiftUp, shiftDown
} from 'store/reducer';
import { getId, save, open, ids, directory as fetchDirectory, saveDirectory } from 'store/filer';
import { getGame } from 'api';

function* fetchGame(action) {
  let state = {};
  yield put(fetchGameStart({ gameId: action.id }));
  if (action.source) {
    state.gameData = yield call(getGame, action.source);
    if (state.gameData) state.gameState = open(getId(state.gameData)).gameState;
  } else {
    state = open(action.id);
  }
  if (!state.gameData) {
    yield put(fetchGameFailed({ gameId: action.id }));
    return;
  }
  yield put(initGame({ ...state, gameId: action.id }));
  yield put(fetchGameSuccess({ gameId: action.id }));
}

function* handleSelect({ position, across }) {
  yield put(selectCell({ position, isAcross: across }));
}

function* handleInput(action) {
  if (action.key === 'ArrowUp') yield put(shiftUp());
  else if (action.key === 'ArrowDown') yield put(shiftDown());
  else if (action.key === 'ArrowLeft') yield put(shiftLeft());
  else if (action.key === 'ArrowRight') yield put(shiftRight());
  else if (action.key === 'Tab') yield put(action.shift ? priorClue() : nextClue());
  else if (action.key === 'Escape') yield put(unselect());
  else if (action.key === 'Backspace') {
    yield put(priorCell({ destructive: true }));
    yield put({ type: events.handleSave });
  } else if (action.key === 'Enter') yield put(toggleDirection());
  else if (action.key === ' ') yield put(action.shift ? priorCell({ destructive: false }) : nextCell());
  else if (action.key.length === 1 && !action.ctrl && !action.meta) {
    yield put(inputLetter({ key: action.key }));
    yield put(nextCell());
    yield put({ type: events.handleSave });
  }
}

function* addDirectory(action) {
  const { directory } = action;
  yield call(saveDirectory, directory);
  yield put(updateDirectory({ directory }));
}

function* loadDirectory(action) {
  const directory = yield call(fetchDirectory);
  yield put(updateDirectory({ directory }));
}

function* handleSave(action) {
  const state = yield select(stateSelector);
  const id = getId(state.gameData);
  yield call(save, state);
  yield put(upsertSaves({ saves: {
    [id]: { ...state.gameData, id }
  }}));
}

function* handleLoadSaves(action) {
  const gameIds = yield call(ids);
  const saves = Object.fromEntries(
    Object.keys(gameIds).map(id => ([
      id, { ...open(id).gameData, id, }
    ]))
  );
  yield put(upsertSaves({ saves }));
}

function* handleReset({ area }) {
  const isConfirmed = yield call(window.confirm, 'Are you sure?');
  if (!isConfirmed) return;
  yield put(clear({ area }));
  yield put({ type: events.handleSave });
}

function* handleCheck({ area }) {
  yield put(check({ area }));
}

function* handleSolve({ area }) {
  const isConfirmed = yield call(window.confirm, 'Are you sure?');
  if (!isConfirmed) return;
  yield put(solve({ area }));
  yield put({ type: events.handleSave });
}

export default function* sagas() {
  yield all([
    takeLatest(events.fetchGame, fetchGame),
    takeLatest(events.handleInput, handleInput),
    takeLatest(events.handleSelect, handleSelect),
    takeLatest(events.addDirectory, addDirectory),
    takeLatest(events.loadDirectory, loadDirectory),
    takeLatest(events.handleSave, handleSave),
    takeLatest(events.loadSaves, handleLoadSaves),
    takeLatest(events.reset, handleReset),
    takeLatest(events.check, handleCheck),
    takeLatest(events.solve, handleSolve),
  ]);
}
